import { Component, OnInit } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { finalize } from 'rxjs/operators';

import { Land, StammdatenService } from '@app/services/stammdaten.service';
import { ReportData, ReportTimeline, ReportsService } from '@app/services/reports.service';

const CHART_PALETTE = [
  '#2563eb',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#ea580c',
  '#4f46e5',
];

const STATUS_LABELS: { [code: string]: string } = {
  E: 'Erfasst',
  T: 'Teil-Erfasst',
  U: 'Überfällig',
  B: 'Bezahlt',
  A: 'Angezeigt',
  AB: 'Anzeige / Bezahlt',
  S: 'Storno',
  '?': 'Unbekannt',
};

type DateRangePreset = 'week' | 'month' | 'months3' | 'months6' | 'year' | 'all' | 'custom';

interface DateRangePresetOption {
  id: DateRangePreset;
  label: string;
}

const DATE_RANGE_PRESETS: DateRangePresetOption[] = [
  { id: 'week', label: 'Diese Woche' },
  { id: 'month', label: 'Dieser Monat' },
  { id: 'months3', label: '3 Monate' },
  { id: 'months6', label: '6 Monate' },
  { id: 'year', label: 'Jahr' },
  { id: 'all', label: 'Gesamt' },
  { id: 'custom', label: 'Ausgewählt' },
];

const GESAMT_FROM_DATE = '2000-01-01';

@Component({
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  reports: ReportData | null = null;
  selectedCountry = '';
  countryOptions: Land[] = [];
  datePreset: DateRangePreset = 'month';
  datePresets = DATE_RANGE_PRESETS;
  fromDate = '';
  toDate = '';
  periodOffset = 0;

  isLoading = false;
  hasError = false;
  errorMessage = '';

  monthlyChartData: ChartData<'line' | 'bar'> = { labels: [], datasets: [] };
  trendChartType: 'line' | 'bar' = 'line';
  trendChartTitle = 'Mandate pro Monat';
  private lastTimeline: ReportData['timeline'] | null = null;
  tatbestandeChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  statusChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  bomChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  countryChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  dienstnehmerChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  weekdayChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  baseChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom' },
    },
  };

  lineChartOptions: ChartOptions = {
    ...this.baseChartOptions,
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: {
      legend: { display: true, position: 'bottom' },
    },
  };

  trendBarChartOptions: ChartOptions = {
    ...this.baseChartOptions,
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: {
      legend: { display: true, position: 'bottom' },
    },
  };

  get trendChartOptions(): ChartOptions {
    return this.trendChartType === 'line' ? this.lineChartOptions : this.trendBarChartOptions;
  }

  barChartOptions: ChartOptions = {
    ...this.baseChartOptions,
    indexAxis: 'y',
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: {
      legend: { display: false },
    },
  };

  verticalBarChartOptions: ChartOptions = {
    ...this.baseChartOptions,
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: {
      legend: { display: false },
    },
  };

  doughnutChartOptions: ChartOptions = {
    ...this.baseChartOptions,
    plugins: {
      legend: { display: true, position: 'bottom' },
    },
  };

  constructor(
    private reportsService: ReportsService,
    private stammdatenService: StammdatenService
  ) {}

  ngOnInit(): void {
    this.loadCountryOptions();
    this.applyDatePreset('month', false);
    this.fetchReports();
  }

  private loadCountryOptions(): void {
    this.stammdatenService.getStammdaten().subscribe(
      (response) => {
        const lander = response?.data?.lander;
        this.countryOptions = this.sortByBezeichnung(
          Array.isArray(lander) ? lander.filter((l) => l.usage !== 0) : []
        );
      },
      () => {
        this.countryOptions = [];
      }
    );
  }

  private sortByBezeichnung(arr: Land[]): Land[] {
    return [...arr].sort((a, b) =>
      ('' + (a?.bezeichnung ?? '')).localeCompare('' + (b?.bezeichnung ?? ''), 'de')
    );
  }

  get isCustomDateRange(): boolean {
    return this.datePreset === 'custom';
  }

  get canNavigatePeriod(): boolean {
    return this.datePreset === 'week' || this.datePreset === 'month';
  }

  get canShiftPeriodForward(): boolean {
    return this.periodOffset < 0;
  }

  get periodRangeLabel(): string {
    if (this.datePreset === 'month' && this.fromDate) {
      const date = new Date(this.fromDate + 'T00:00:00');
      const months = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return `${this.formatDateDisplay(this.fromDate)} – ${this.formatDateDisplay(this.toDate)}`;
  }

  selectDatePreset(preset: DateRangePreset): void {
    if (preset === 'custom') {
      this.datePreset = 'custom';
      this.periodOffset = 0;
      return;
    }
    this.periodOffset = 0;
    this.applyDatePreset(preset, true);
  }

  shiftPeriod(direction: -1 | 1): void {
    if (!this.canNavigatePeriod) {
      return;
    }
    if (direction === 1 && !this.canShiftPeriodForward) {
      return;
    }
    this.periodOffset += direction;
    this.applyDatePreset(this.datePreset, true);
  }

  onCustomDateChange(): void {
    this.datePreset = 'custom';
  }

  formatDateDisplay(iso: string): string {
    if (!iso) {
      return '';
    }
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  }

  private applyDatePreset(preset: DateRangePreset, fetch: boolean): void {
    if (preset === 'custom') {
      return;
    }

    const range = this.getPresetRange(preset);
    this.datePreset = preset;
    this.fromDate = range.from;
    this.toDate = range.to;

    if (fetch) {
      this.fetchReports();
    }
  }

  private getPresetRange(preset: DateRangePreset): { from: string; to: string } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'week': {
        const ref = new Date(today);
        ref.setDate(ref.getDate() + this.periodOffset * 7);
        const monday = this.startOfWeek(ref);
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);
        return { from: this.toIsoDate(monday), to: this.toIsoDate(sunday) };
      }
      case 'month': {
        const ref = new Date(today.getFullYear(), today.getMonth() + this.periodOffset, 1);
        const start = new Date(ref.getFullYear(), ref.getMonth(), 1);
        const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
        return { from: this.toIsoDate(start), to: this.toIsoDate(end) };
      }
      case 'months3': {
        const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        return { from: this.toIsoDate(start), to: this.toIsoDate(today) };
      }
      case 'months6': {
        const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        return { from: this.toIsoDate(start), to: this.toIsoDate(today) };
      }
      case 'year': {
        const start = new Date(today.getFullYear(), 0, 1);
        return { from: this.toIsoDate(start), to: this.toIsoDate(today) };
      }
      case 'all':
        return { from: GESAMT_FROM_DATE, to: this.toIsoDate(today) };
      default:
        return { from: this.fromDate, to: this.toDate };
    }
  }

  private startOfWeek(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const daysSinceMonday = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - daysSinceMonday);
    return d;
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  get hasData(): boolean {
    return !!this.reports && (this.reports.kpis?.total ?? 0) > 0;
  }

  get kpis() {
    return (
      this.reports?.kpis ?? {
        total: 0,
        anzeigen: 0,
        bom: 0,
        aktiv_offen: 0,
        im_zeitraum: 0,
      }
    );
  }


  fetchReports(): void {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    const country = this.selectedCountry?.trim() || undefined;

    this.reportsService
      .getReports(this.fromDate, this.toDate, country, this.getTimelineGranularity())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.reports = data;
          this.updateCharts(data);
        },
        error: (err) => {
          this.hasError = true;
          this.errorMessage =
            err?.error?.msg || err?.message || 'Berichte konnten nicht geladen werden.';
          this.reports = null;
        },
      });
  }

  private colors(n: number): string[] {
    return Array.from({ length: n }, (_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);
  }

  setTrendChartType(type: 'line' | 'bar'): void {
    this.trendChartType = type;
    this.updateTrendChart();
  }

  private getTimelineGranularity(): 'day' | 'month' {
    if (this.datePreset === 'week' || this.datePreset === 'month') {
      return 'day';
    }
    if (this.datePreset === 'months3' || this.datePreset === 'months6') {
      return 'month';
    }
    if (this.datePreset === 'custom') {
      return this.daysBetween(this.fromDate, this.toDate) <= 45 ? 'day' : 'month';
    }
    return 'month';
  }

  private daysBetween(from: string, to: string): number {
    if (!from || !to) {
      return 0;
    }
    const start = new Date(from + 'T00:00:00');
    const end = new Date(to + 'T00:00:00');
    const diff = end.getTime() - start.getTime();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)) + 1);
  }

  private formatDayLabel(iso: string): string {
    const date = new Date(iso + 'T00:00:00');
    const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${weekdays[date.getDay()]} ${day}.${month}.`;
  }

  private formatMonthLabel(ym: string): string {
    const [y, m] = ym.split('-');
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const idx = parseInt(m, 10) - 1;
    return `${months[idx] ?? m} ${y}`;
  }

  private updateTrendChart(): void {
    const timeline = this.lastTimeline;
    if (!timeline?.keys?.length) {
      this.monthlyChartData = { labels: [], datasets: [] };
      return;
    }

    this.trendChartTitle =
      timeline.granularity === 'day' ? 'Mandate pro Tag' : 'Mandate pro Monat';

    const labels =
      timeline.granularity === 'day'
        ? timeline.keys.map((k) => this.formatDayLabel(k))
        : timeline.keys.map((k) => this.formatMonthLabel(k));

    const anzeigeColor = CHART_PALETTE[0];
    const bomColor = CHART_PALETTE[3];

    if (this.trendChartType === 'line') {
      this.monthlyChartData = {
        labels,
        datasets: [
          {
            data: timeline.anzeige,
            label: 'Anzeigen',
            borderColor: anzeigeColor,
            backgroundColor: anzeigeColor + '33',
            fill: false,
            tension: 0,
          },
          {
            data: timeline.bom,
            label: 'BOM',
            borderColor: bomColor,
            backgroundColor: bomColor + '33',
            fill: false,
            tension: 0,
          },
        ],
      };
      return;
    }

    this.monthlyChartData = {
      labels,
      datasets: [
        {
          data: timeline.anzeige,
          label: 'Anzeigen',
          backgroundColor: anzeigeColor,
        },
        {
          data: timeline.bom,
          label: 'BOM',
          backgroundColor: bomColor,
        },
      ],
    };
  }

  private buildTimelineFromMonthly(data: ReportData): ReportTimeline {
    const monthlyKeys = Object.keys(data.monthly || {}).sort();
    const values = monthlyKeys.map((k) => data.monthly[k]);
    return {
      granularity: 'month',
      keys: monthlyKeys,
      anzeige: values,
      bom: monthlyKeys.map(() => 0),
    };
  }

  updateCharts(data: ReportData): void {
    this.lastTimeline = data.timeline ?? this.buildTimelineFromMonthly(data);
    this.updateTrendChart();

    const tatTop = data.tatbestande_top || [];
    this.tatbestandeChartData = {
      labels: tatTop.map((t) => t.label),
      datasets: [
        {
          data: tatTop.map((t) => t.count),
          label: 'Tatbestände',
          backgroundColor: this.colors(tatTop.length),
        },
      ],
    };

    const statusKeys = Object.keys(data.status_count || {});
    this.statusChartData = {
      labels: statusKeys.map((k) => STATUS_LABELS[k] || k),
      datasets: [
        {
          data: statusKeys.map((k) => data.status_count[k]),
          backgroundColor: this.colors(statusKeys.length),
        },
      ],
    };

    const bom = data.bom_split || { anzeige: 0, bom: 0 };
    this.bomChartData = {
      labels: ['Anzeige', 'BOM'],
      datasets: [
        {
          data: [bom.anzeige, bom.bom],
          backgroundColor: [CHART_PALETTE[0], CHART_PALETTE[3]],
        },
      ],
    };

    const countryEntries = Object.entries(data.country_count || {}).sort((a, b) => b[1] - a[1]);
    this.countryChartData = {
      labels: countryEntries.map(([k]) => k),
      datasets: [
        {
          data: countryEntries.map(([, v]) => v),
          label: 'Land',
          backgroundColor: this.colors(countryEntries.length),
        },
      ],
    };

    const dnTop = data.dienstnehmer_top || [];
    this.dienstnehmerChartData = {
      labels: dnTop.map((d) => d.label),
      datasets: [
        {
          data: dnTop.map((d) => d.count),
          label: 'Dienstnehmer',
          backgroundColor: this.colors(dnTop.length),
        },
      ],
    };

    const weekdayOrder = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    const weekdayLabels = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    const wc = data.weekday_count || {};
    this.weekdayChartData = {
      labels: weekdayLabels,
      datasets: [
        {
          data: weekdayOrder.map((k) => wc[k] ?? 0),
          label: 'Mandate',
          backgroundColor: CHART_PALETTE[0],
        },
      ],
    };
  }
}
